import { useFormContext } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import type { RuntimeQuestion } from '@/lib/questionnaireFormSchema';
import { MultiSelect } from '@/components/ui/multi-select';

interface QuestionFieldProps {
  question: RuntimeQuestion;
  defaultValue?: string | string[];
}

export function QuestionField({ question, defaultValue }: QuestionFieldProps) {
  const { control } = useFormContext();

  const renderField = () => {
    switch (question.question_type) {
      case 'string':
        return (
          <FormField
            control={control}
            name={question.key}
            defaultValue={defaultValue || ''}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {question.label}
                  {question.required && (
                    <span className="ml-1 text-red-500">*</span>
                  )}
                </FormLabel>
                <FormControl>
                  <Input placeholder={question.placeholder || ''} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'text':
        return (
          <FormField
            control={control}
            name={question.key}
            defaultValue={defaultValue || ''}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {question.label}
                  {question.required && (
                    <span className="ml-1 text-red-500">*</span>
                  )}
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={question.placeholder || ''}
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'radio':
        return (
          <FormField
            control={control}
            name={question.key}
            defaultValue={defaultValue || ''}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {question.label}
                  {question.required && (
                    <span className="ml-1 text-red-500">*</span>
                  )}
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    {question.options?.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2"
                      >
                        <RadioGroupItem
                          value={option.value}
                          id={`${question.key}-${option.value}`}
                        />
                        <Label
                          htmlFor={`${question.key}-${option.value}`}
                          className="text-sm cursor-pointer"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'select':
        return (
          <FormField
            control={control}
            name={question.key}
            defaultValue={defaultValue || ''}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {question.label}
                  {question.required && (
                    <span className="ml-1 text-red-500">*</span>
                  )}
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value || ''}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={
                        question.placeholder || 'Select your option here'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {question.options?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'multi_select':
        return (
          <FormField
            control={control}
            name={question.key}
            defaultValue={defaultValue || []}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {question.label}
                  {question.required && (
                    <span className="ml-1 text-red-500">*</span>
                  )}
                </FormLabel>
                {question.max_selections && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Select up to {question.max_selections} options
                  </p>
                )}
                <FormControl>
                  <MultiSelect
                    options={question.options || []}
                    onValueChange={field.onChange}
                    defaultValue={field.value || []}
                    placeholder={
                      question.placeholder || 'Select your options here'
                    }
                    variant={'inverted'}
                    maxCount={3}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      default:
        return (
          <div className="p-4 border border-red-200 rounded-md bg-red-50">
            <p className="text-sm text-red-600">
              Unsupported question type: {question.question_type}
            </p>
          </div>
        );
    }
  };

  return <div className="space-y-2">{renderField()}</div>;
}
